import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { AuthProvider } from './context/AuthContext';
import { PermissionProvider } from './context/PermissionContext';
import { DocumentTypesProvider } from './context/DocumentTypesContext';
import { CombinedCacheProvider } from './context/CombinedCacheProvider';
import { QueryInspectorProvider } from './context/QueryInspectorContext';
import { ToastProvider } from './context/ToastContext';
import { ShortcutProvider } from './context/ShortcutContext';
import { OpenTabsProvider } from './context/OpenTabsContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingFallback from './components/LoadingFallback';
import Redirector from './components/Redirector';
import KeepAliveOutlet from './components/KeepAliveOutlet';

// Excluded routes that must always mount/unmount normally (no keep-alive).
const LandingPage = lazyWithRetry(() => import('./screens/LandingPage'));
const ResetPassword = lazyWithRetry(() => import('./screens/ResetPassword'));
const ChangePassword = lazyWithRetry(() => import('./screens/ChangePassword'));

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router>
          <AuthProvider>
            <PermissionProvider>
              <ToastProvider>
                <DocumentTypesProvider>
                  <ShortcutProvider>
                    <OpenTabsProvider>
                    <CombinedCacheProvider>
                    <QueryInspectorProvider>
                    <Redirector />
                    <Suspense fallback={<LoadingFallback />}>
                      {/*
                       * Excluded routes — these paths are NOT open-tab screens.
                       * They mount/unmount normally via a standard <Routes>.
                       * EXCLUDED_PATHS / EXCLUDED_PREFIXES in OpenTabsContext
                       * keep them out of the tab strip.
                       */}
                      <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/login" element={<LandingPage />} />
                        <Route path="/reset-password/:token" element={<ResetPassword />} />
                        <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
                        {/* Silences the dev "No routes matched" warning on every non-excluded
                            path — real screens render via KeepAliveOutlet below. */}
                        <Route path="*" element={null} />
                      </Routes>
                      {/*
                       * Keep-alive renderer — mounts all open-tab screens
                       * simultaneously; active tab is visible, inactive are
                       * hidden with display:none (never unmounted).
                       */}
                      <KeepAliveOutlet />
                    </Suspense>
                    </QueryInspectorProvider>
                    </CombinedCacheProvider>
                    </OpenTabsProvider>
                  </ShortcutProvider>
                </DocumentTypesProvider>
              </ToastProvider>
            </PermissionProvider>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
